import type { AdmissionsCitation } from './types';
import {
  inferEntityKindFromToken,
  isIdentifierToken,
  parseCitationKey,
  resolveCitationApplicationId,
  resolveCitationEntityLabel,
  resolveCitationEntityKind,
  resolveCitationHref,
  type CitationEntityKind,
  type CitationRouteTarget,
} from './citation-routing';

const PROTECTED_MARKDOWN_PATTERN = /(\[[^\]]+\]\([^)]+\)|\[Source\s+\d+\]|\[Sources\s+\d+\s*&\s*\d+\])/g;
const IDENTIFIER_TOKEN_PATTERN = /\b(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|(?:APP|FA|FAM|STU|RES|DOC)-[A-Z0-9]{2,}(?:-[A-Z0-9]{2,})*)\b/gi;
const PROTECTED_MARKDOWN_SEGMENT_PATTERN = /^\[[^\]]+\]\([^)]+\)$|^\[Source\s+\d+\]$|^\[Sources\s+\d+\s*&\s*\d+\]$/i;

type TokenDescriptor = {
  href: string | null;
  kind: CitationEntityKind;
  applicationCode: string | null;
  entityLabel: string | null;
};

const toOptionalText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const toRedactedLabel = (
  kind: CitationEntityKind,
  options?: {
    applicationCode?: string | null;
    allowApplicationCode?: boolean;
    entityLabel?: string | null;
    targetKind?: CitationRouteTarget['kind'];
  }
): string => {
  const entityLabel = toOptionalText(options?.entityLabel);
  if (entityLabel) {
    return entityLabel;
  }
  const targetKind = options?.targetKind ?? (kind === 'reference' ? 'application' : kind);
  if (kind === 'application') {
    if (!options?.targetKind && options?.allowApplicationCode && options.applicationCode) {
      return `Application ${options.applicationCode}`;
    }
    return targetKind === 'application' ? 'Open application' : 'Application';
  }
  if (kind === 'family') return targetKind === 'family' ? 'Open family details' : 'Family';
  if (kind === 'student') return targetKind === 'student' ? 'Open student details' : 'Student';
  if (kind === 'resource') return targetKind === 'resource' ? 'Open resource' : 'Resource';
  return 'Reference';
};

const buildTokenIndex = (citations: AdmissionsCitation[]): Map<string, TokenDescriptor> => {
  const byToken = new Map<string, TokenDescriptor>();

  const upsert = (token: string | null, descriptor: TokenDescriptor): void => {
    const normalizedToken = String(token ?? '').trim();
    if (!normalizedToken || !isIdentifierToken(normalizedToken)) {
      return;
    }
    const key = normalizedToken.toLowerCase();
    const existing = byToken.get(key);
    if (!existing || (!existing.href && descriptor.href)) {
      byToken.set(key, descriptor);
    }
  };

  citations.forEach((citation) => {
    const key = parseCitationKey(citation);
    const defaultKind = resolveCitationEntityKind(citation);
    const defaultHref = resolveCitationHref(citation, defaultKind);
    const applicationCode = toOptionalText(citation.applicationCode);
    const entityLabel = resolveCitationEntityLabel(citation);
    const applicationId = resolveCitationApplicationId(citation);
    const table = String(citation.table ?? '').toLowerCase();
    const keyId = toOptionalText(key.id);

    upsert(applicationId, {
      href: resolveCitationHref(citation, 'application'),
      kind: 'application',
      applicationCode,
      entityLabel,
    });

    if (keyId && (table.includes('application') || keyId === applicationId)) {
      upsert(keyId, {
        href: resolveCitationHref(citation, 'application'),
        kind: 'application',
        applicationCode,
        entityLabel,
      });
    }

    upsert(toOptionalText(key.applicationId), {
      href: resolveCitationHref(citation, 'application'),
      kind: 'application',
      applicationCode,
      entityLabel,
    });

    upsert(toOptionalText(key.familyId), {
      href: resolveCitationHref(citation, 'family'),
      kind: 'family',
      applicationCode,
      entityLabel,
    });

    upsert(toOptionalText(key.studentId), {
      href: resolveCitationHref(citation, 'student'),
      kind: 'student',
      applicationCode,
      entityLabel,
    });

    upsert(toOptionalText(key.resourceId), {
      href: resolveCitationHref(citation, 'resource'),
      kind: 'resource',
      applicationCode,
      entityLabel,
    });

    upsert(toOptionalText(citation.applicationCode), {
      href: resolveCitationHref(citation, 'application'),
      kind: 'application',
      applicationCode,
      entityLabel,
    });

    const entityCode = toOptionalText(citation.entityCode);
    if (entityCode) {
      const entityKind = inferEntityKindFromToken(entityCode);
      upsert(entityCode, {
        href: resolveCitationHref(citation, entityKind === 'reference' ? defaultKind : entityKind) ?? defaultHref,
        kind: entityKind === 'reference' ? defaultKind : entityKind,
        applicationCode,
        entityLabel,
      });
    }
  });

  return byToken;
};

const linkifyTextSegment = (segment: string, byToken: Map<string, TokenDescriptor>): string => (
  segment.replace(IDENTIFIER_TOKEN_PATTERN, (rawToken) => {
    const descriptor = byToken.get(rawToken.toLowerCase());
    const kind = descriptor?.kind ?? inferEntityKindFromToken(rawToken);
    const descriptorTargetKind = descriptor?.kind === 'reference' ? undefined : descriptor?.kind;
    const targetKind = descriptor?.href
      ? descriptorTargetKind
        ? ({
          application: 'application',
          family: 'family',
          student: 'student',
          resource: 'resource',
        } as const)[descriptorTargetKind]
        : 'application'
      : undefined;
    const label = toRedactedLabel(kind, {
      applicationCode: descriptor?.applicationCode,
      allowApplicationCode: Boolean(descriptor?.href),
      entityLabel: descriptor?.entityLabel,
      targetKind,
    });
    if (descriptor?.href) {
      return `[${label}](${descriptor.href})`;
    }
    return label;
  })
);

const redactTextSegment = (segment: string): string => (
  segment.replace(IDENTIFIER_TOKEN_PATTERN, (rawToken) => (
    toRedactedLabel(inferEntityKindFromToken(rawToken))
  ))
);

export const linkifyAssistantMessageIdentifiers = (
  content: string,
  citations: AdmissionsCitation[] | undefined
): string => {
  if (!content) {
    return '';
  }
  const citationList = Array.isArray(citations) ? citations : [];
  const parts = content.split(PROTECTED_MARKDOWN_PATTERN);
  if (citationList.length === 0) {
    return parts
      .map((part) => (PROTECTED_MARKDOWN_SEGMENT_PATTERN.test(part) ? part : redactTextSegment(part)))
      .join('');
  }
  const byToken = buildTokenIndex(citationList);
  return parts
    .map((part) => (PROTECTED_MARKDOWN_SEGMENT_PATTERN.test(part) ? part : linkifyTextSegment(part, byToken)))
    .join('');
};

export const formatCitationIdentifierLabel = (
  token: string,
  options?: { applicationCode?: string | null; linked?: boolean; entityLabel?: string | null; targetKind?: CitationRouteTarget['kind'] }
): string => (
  toRedactedLabel(inferEntityKindFromToken(token), {
    applicationCode: options?.applicationCode ?? null,
    allowApplicationCode: Boolean(options?.linked),
    entityLabel: options?.entityLabel ?? null,
    targetKind: options?.targetKind,
  })
);
